import { Pencil, Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { SimpleTooltip } from "@/components/ui/tooltip"
import type { TAccountsSummary } from "@/http/finance"
import { cn } from "@/lib/utils"

type TAccountCardProps = {
  account: TAccountsSummary
  onDelete?: (accountId: string) => void
  onEdit?: (accountId: string) => void
  canDelete?: boolean
  canEdit?: boolean
}

export function AccountCard({
  account,
  onDelete,
  onEdit,
  canDelete = false,
  canEdit = false,
}: TAccountCardProps) {
  const balance = account.paid.income - account.paid.outcome

  const predicted =
    account.paid.income -
    account.paid.outcome +
    (account.unpaid.income - account.unpaid.outcome)

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value)

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="flex-1">
          <CardTitle className="text-lg font-semibold">
            {account.name}
          </CardTitle>
          <CardDescription className="text-xs">
            Conta Financeira
          </CardDescription>
        </div>
        <div className="flex gap-1">
          {canEdit && onEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(account.id)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {canDelete && onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onDelete(account.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            "text-2xl font-bold tabular-nums",
            balance > 0 && "text-emerald-400",
            balance < 0 && "text-rose-400",
            balance === 0 && "text-gray-400",
          )}
        >
          {formatCurrency(balance)}
        </div>
        <SimpleTooltip
          title="Saldo previsto considerando transações pendentes até o fim do mês"
          fullWidth={false}
        >
          <p
            className={cn(
              "text-sm text-muted-foreground mt-1 opacity-70 tabular-nums",
              predicted > 0 && "text-emerald-400",
              predicted < 0 && "text-rose-400",
              predicted === 0 && "text-gray-400",
            )}
          >
            {formatCurrency(predicted)}
          </p>
        </SimpleTooltip>
      </CardContent>
    </Card>
  )
}
